/**
 * DescriptionPanel.tsx
 * Shows node help content and selected node state.
 */
import type { AppNode, RubyPart } from '../types';
import {
  isCounterNode,
  isDanceNode,
  isGestureNode,
  isIfNode,
  isKeywordSwitchNode,
  isLightNode,
  isRandomChoiceNode,
  isSoundEffectNode,
  isSwitchNode,
  isTextEditNode,
  isWaitNode,
} from '../types';
import { RubyText } from '../rubyText';
import { getGestureLabel } from '../config/gestureMotions';
import { getDanceLabel } from '../config/danceMotions';
import { useFlowDisplayLanguage } from '../displayLanguage';
import { IF_OPERATOR_LABELS } from '../nodes/settings/IfSettings';
import {
  DEFAULT_SOUND_EFFECT,
  getSoundEffectLabel,
  isSoundEffect,
} from '../config/soundEffects';
import {
  DEFAULT_LIGHT_COLOR,
  getLightLabel,
  isLightColor,
} from '../config/lightPresets';

export type DescriptionPanelHelpInfo = {
  title: RubyPart[];
  description: RubyPart[];
  example?: RubyPart[];
};

type DescriptionPanelProps = {
  help?: DescriptionPanelHelpInfo;
  selectedNode?: AppNode;
};

export function DescriptionPanel({ help, selectedNode }: DescriptionPanelProps) {
  const displayLanguage = useFlowDisplayLanguage();

  return (
    <div className='flex h-full flex-col bg-card'>
      <div className='border-b border-border bg-muted/60 px-4 py-2'>
        <h2 className='text-base font-semibold text-slate-700'>説明</h2>
      </div>
      <div className='flex-1 overflow-auto p-4'>
        {!help ? (
          <p className='text-base leading-relaxed text-muted-foreground'>カードをクリックすると、ここに説明が出ます。</p>
        ) : (
          <div className='space-y-3'>
            <h3 className='text-lg font-semibold text-foreground'>
              <RubyText parts={help.title} />
            </h3>
            <p className='text-base leading-relaxed text-foreground'>
              <RubyText parts={help.description} />
            </p>
            {help.example && (
              <p className='text-base text-muted-foreground'>
                <RubyText parts={help.example} />
              </p>
            )}
            {selectedNode && isTextEditNode(selectedNode) && (
              <div className='rounded-lg bg-emerald-50 p-2 text-base text-emerald-700'>
                <span className='font-medium'>今のことば：</span>
                {selectedNode.data.text || '（まだ入力されていません）'}
              </div>
            )}
            {selectedNode && isGestureNode(selectedNode) && (
              <div className='rounded-lg bg-violet-50 p-2 text-base text-violet-700'>
                <span className='font-medium'>今のうごき：</span>
                {getGestureLabel(selectedNode.data.motionId) || '（まだ選ばれていません）'}
              </div>
            )}
            {selectedNode && isDanceNode(selectedNode) && (
              <div className='rounded-lg bg-rose-50 p-2 text-base text-rose-700'>
                <span className='font-medium'>今のダンス：</span>
                {getDanceLabel(selectedNode.data.motionId) || '（まだ選ばれていません）'}
              </div>
            )}
            {selectedNode && isCounterNode(selectedNode) && (
              <div className='rounded-lg bg-amber-50 p-2 text-base text-amber-700'>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'start number:' : 'はじめの数：'}</span> {selectedNode.data.initialValue}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'count by:' : 'ふえる数：'}</span> {selectedNode.data.stepValue}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'end number:' : 'おわりの数：'}</span> {selectedNode.data.finalValue}</div>
              </div>
            )}
            {selectedNode && isSwitchNode(selectedNode) && (
              <div className='rounded-lg bg-indigo-50 p-2 text-base text-indigo-700'>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'word 1:' : 'ことば1：'}</span> {selectedNode.data.matchValue1 || '""'}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'word 2:' : 'ことば2：'}</span> {selectedNode.data.matchValue2 || '""'}</div>
              </div>
            )}
            {selectedNode && isIfNode(selectedNode) && (
              <div className='rounded-lg bg-teal-50 p-2 text-base text-teal-700'>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'condition operator:' : '比較の条件：'}</span> {IF_OPERATOR_LABELS[displayLanguage][selectedNode.data.operator]}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'value to compare:' : '比較する値：'}</span> {selectedNode.data.compareValue || '""'}</div>
              </div>
            )}
            {selectedNode && isWaitNode(selectedNode) && (
              <div className='rounded-lg bg-blue-50 p-2 text-base text-blue-700'>
                <span className='font-medium'>{displayLanguage === 'en' ? 'seconds:' : '秒数：'}</span>{' '}
                {Number.isFinite(selectedNode.data.seconds) ? selectedNode.data.seconds : 1}
              </div>
            )}
            {selectedNode && isRandomChoiceNode(selectedNode) && (
              <div className='rounded-lg bg-red-50 p-2 text-base text-red-700'>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'choice 1:' : '候補1：'}</span> {selectedNode.data.choice1 ?? 'A'}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'choice 2:' : '候補2：'}</span> {selectedNode.data.choice2 ?? 'B'}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'choice 3:' : '候補3：'}</span> {selectedNode.data.choice3 ?? 'C'}</div>
              </div>
            )}
            {selectedNode && isKeywordSwitchNode(selectedNode) && (
              <div className='rounded-lg bg-teal-50 p-2 text-base text-teal-700'>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'keyword 1:' : 'キーワード1：'}</span> {selectedNode.data.keyword1 || '""'}</div>
                <div><span className='font-medium'>{displayLanguage === 'en' ? 'keyword 2:' : 'キーワード2：'}</span> {selectedNode.data.keyword2 || '""'}</div>
              </div>
            )}
            {selectedNode && isSoundEffectNode(selectedNode) && (
              <div className='rounded-lg bg-orange-50 p-2 text-base text-orange-700'>
                <span className='font-medium'>{displayLanguage === 'en' ? 'sound:' : '効果音：'}</span>{' '}
                {getSoundEffectLabel(
                  isSoundEffect(selectedNode.data.sound) ? selectedNode.data.sound : DEFAULT_SOUND_EFFECT,
                  displayLanguage,
                )}
              </div>
            )}
            {selectedNode && isLightNode(selectedNode) && (
              <div className='rounded-lg bg-yellow-50 p-2 text-base text-yellow-700'>
                <span className='font-medium'>{displayLanguage === 'en' ? 'color:' : '色：'}</span>{' '}
                {getLightLabel(
                  isLightColor(selectedNode.data.color) ? selectedNode.data.color : DEFAULT_LIGHT_COLOR,
                  displayLanguage,
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
